/**
 * Keşif — bir taramayı ve tüm veri kümesini silme onayı.
 *
 * Silme FK cascade ile gider: sayfalar, varlıklar, kenarlar, frontier. Geri
 * dönüşü yoktur ve yeniden toplamanın bedeli gerçektir (dakikalar süren bir
 * tarama, indirilmiş megabaytlar, harcanmış LLM çağrısı). Bu yüzden onay
 * metni ne kaybedileceğini SAYIYLA söyler — "emin misiniz?" kullanıcıya
 * kararı verecek bilgiyi vermez.
 *
 * Korpusa yazılmış kanıt satırları taramaya ait DEĞİLDİR ve silinmez; bunu da
 * söylemek gerekir, aksi halde kullanıcı münazara kanıtını kaybettiğini sanır.
 */
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useKesifStore } from "../../store/kesifStore";
import { Btn, ErrorBanner, Modal } from "../library/ui";
import type { CrawlJob } from "../../domain/kesif";
import { formatBytes, isTerminalStatus } from "../../domain/kesif";

export function DeleteJobModal({
  job,
  onClose,
  onDeleted,
}: {
  job: CrawlJob;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const remove = useKesifStore((s) => s.remove);
  const cancel = useKesifStore((s) => s.cancel);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const c = job.counters;
  const live = !isTerminalStatus(job.status);

  async function confirm() {
    setBusy(true);
    setError(null);
    const ok = await remove(job.id);
    setBusy(false);
    if (!ok) {
      setError(useKesifStore.getState().listError ?? "Silinemedi.");
      return;
    }
    onDeleted();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Taramayı sil"
      subtitle={`«${job.name || job.seedUrls[0]}» ve topladığı her şey kalıcı olarak silinir.`}
      width="max-w-lg"
    >
      <div className="space-y-4">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <ul className="divide-y divide-white/[0.05] overflow-hidden rounded-xl border border-white/[0.07] text-[12px]">
          <Row label="Sayfa" value={`${c.fetched.toLocaleString("tr-TR")} kayıt`} />
          <Row
            label="Varlık"
            value={`${c.assetsSaved.toLocaleString("tr-TR")} dosya · ${formatBytes(c.assetBytes)}`}
          />
          <Row label="İndirilen veri" value={formatBytes(c.bytesFetched + c.assetBytes)} />
        </ul>

        <p className="text-[11.5px] leading-relaxed text-slate-500">
          Korpusa yazılan{" "}
          <span className="text-slate-300">{c.corpusRows.toLocaleString("tr-TR")} kanıt</span>{" "}
          satırı silinmez — bunlar münazara kanıt havuzuna aittir ve Kanıt sekmesinden
          ayrıca yönetilir.
        </p>

        {live && (
          <p className="rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2 text-[11.5px] leading-relaxed text-amber-200/90">
            Bu tarama hâlâ çalışıyor. Çalışan bir iş silinemez; önce iptal edin.
          </p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-white/[0.06] pt-3">
          <Btn tone="ghost" size="sm" onClick={onClose} disabled={busy}>
            Vazgeç
          </Btn>
          {live ? (
            <Btn
              tone="outline"
              size="sm"
              onClick={() => {
                // Kapat: iptal işbirlikçidir, anında bitmez. Modal açık
                // kalırsa bayat `running` durumunu göstermeye devam eder ve
                // «Sil» hâlâ erişilemez görünür. Başlıktaki durum etiketi
                // gerçek geçişi anlatır; kullanıcı bittiğinde tekrar açar.
                void cancel(job.id);
                onClose();
              }}
              disabled={busy}
            >
              Önce iptal et
            </Btn>
          ) : (
            <Btn tone="danger" size="sm" onClick={() => void confirm()} loading={busy}>
              {!busy && <Trash2 className="h-3.5 w-3.5" />}
              Kalıcı olarak sil
            </Btn>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-slate-500">{label}</span>
      <span className="tabular-nums text-slate-200">{value}</span>
    </li>
  );
}
